
import React, { useState } from 'react';
import { campo } from './ui/campo';
import { useIdioma } from '../i18n/idioma';
import toast from 'react-hot-toast';
import { useCRM } from '../contexts/CRMContext';
import { useProjects } from '../contexts/ProjectsContext';
import { Client, ProjectStatus } from '../types';
import { X, Search, Briefcase, Calendar, DollarSign, Loader2, ArrowRight } from 'lucide-react';
import { todayLocalDateString, formatLocalDateString } from '../hooks/useCalendarEvents';
import { TOAST, surfaceAddProjectResult } from '../services/toastCopy';
import { Button } from './ui/Button';
import { SearchInput } from './ui/SearchInput';
import { EmptyState } from './ui/EmptyState';

interface NewProjectModalProps {
    onClose: () => void;
}

const NewProjectModal: React.FC<NewProjectModalProps> = ({ onClose }) => {
    const { t } = useIdioma();
    const { clients } = useCRM();
    const { addProject } = useProjects();
    const [step, setStep] = useState(1);
    const [search, setSearch] = useState('');
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [isCreating, setIsCreating] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        // Held as a string so the field can start (and stay) empty. As a number
        // it rendered a literal "0" that had to be deleted before typing, and
        // clearing the box snapped it straight back to 0.
        revenue: '',
        status: 'Scheduled' as ProjectStatus,
        // Días LOCALES. Con `toISOString()` una obra creada a las 6 de la tarde
        // en Las Vegas arrancaba MAÑANA: en UTC ya es el día siguiente.
        startDate: todayLocalDateString(),
        endDate: formatLocalDateString(new Date(Date.now() + 14 * 86400000)),
        description: ''
    });

    const filteredClients = clients.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.phone.includes(search)
    ).slice(0, 5);

    const handleCreate = async () => {
        if (!selectedClient) return;
        setIsCreating(true);
        try {
            const result = await addProject({
                clientId: selectedClient.id,
                clientName: selectedClient.name,
                // No estimateId for manual project — column is nullable per migration 014
                name: formData.name || t('proyecto.nombrePorDefecto', { cliente: selectedClient.name }),
                startDate: new Date(formData.startDate).toISOString(),
                endDate: new Date(formData.endDate).toISOString(),
                // Empty or a lone "." parses to NaN/0 — fall back to 0 so the
                // project is never created with a NaN contract value.
                revenue: Number(formData.revenue) || 0,
                status: formData.status,
                description: formData.description,
            });

            surfaceAddProjectResult(result);
            onClose();
        } catch (err: any) {
            toast.error(TOAST.project.createFailed(err?.message || t('proyNuevo.errorDesconocido')));
        } finally {
            setIsCreating(false);
        }
    };

    const inputClass = campo({ alto: 'auto' });
    const labelClass = "label-ui block mb-1.5";

    return (
        <div className="fixed inset-0 bg-navy-950/50 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="bg-white rounded-3xl shadow-s3 w-full max-w-lg max-h-[90dvh] overflow-hidden flex flex-col animate-in zoom-in-95 fade-in duration-300">
                <div className="px-5 py-4 border-b border-acero-200 flex justify-between items-center">
                    <h3 className="text-lg font-semibold tracking-tight text-navy-950">{t('proyNuevo.titulo')}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('proyNuevo.cerrar')}
                        className="p-2 rounded-lg text-peligro hover:bg-peligro-bg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* El botón de crear va DENTRO de este cuerpo, al final del paso
                    2. Sin `overflow-y-auto` aquí, en una ventana baja el panel
                    crecía más que la pantalla, el `overflow-hidden` de arriba lo
                    recortaba y ese botón quedaba fuera de alcance: el formulario
                    se podía llenar pero no guardar. */}
                <div className="p-6 flex-1 min-h-0 overflow-y-auto custom-scrollbar">
                    {step === 1 ? (
                        <div className="space-y-4">
                            <p className="text-sm text-acero-500">{t('proyNuevo.escogeCliente')}</p>
                            <SearchInput
                                autoFocus
                                value={search}
                                onChange={setSearch}
                                placeholder={t('proyNuevo.buscar.placeholder')}
                                className="w-full"
                            />
                            <div className="max-h-[300px] overflow-y-auto divide-y divide-acero-100 border-y border-acero-200">
                                {filteredClients.map(client => (
                                    <button
                                        key={client.id}
                                        type="button"
                                        onClick={() => { setSelectedClient(client); setStep(2); setFormData({ ...formData, name: t('proyecto.nombrePorDefecto', { cliente: client.name }) }); }}
                                        className="group w-full text-left px-3 py-3 flex items-center justify-between gap-3 hover:bg-acero-50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                    >
                                        <span className="min-w-0">
                                            <span className="block text-sm font-medium text-navy-950 truncate">{client.name}</span>
                                            <span className="block text-xs text-acero-500 truncate">{client.address}</span>
                                        </span>
                                        <ArrowRight size={16} className="shrink-0 text-acero-500 group-hover:text-navy-700 transition-colors" />
                                    </button>
                                ))}
                                {filteredClients.length === 0 && search && (
                                    <EmptyState
                                        icon={<Search size={18} />}
                                        variant="filtro"
                                        title={t('proyNuevo.vacio.titulo')}
                                        description={t('proyNuevo.vacio.texto')}
                                        className="!py-10"
                                    />
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <label className={labelClass}>{t('proyNuevo.campo.nombre')}</label>
                                <input
                                    className={inputClass}
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>{t('proyNuevo.campo.inicio')}</label>
                                    <input
                                        type="date"
                                        className={`${inputClass} font-data tabular-nums`}
                                        value={formData.startDate}
                                        onChange={e => setFormData({ ...formData, startDate: e.target.value })}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>{t('proyNuevo.campo.fin')}</label>
                                    <input
                                        type="date"
                                        className={`${inputClass} font-data tabular-nums`}
                                        value={formData.endDate}
                                        onChange={e => setFormData({ ...formData, endDate: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>{t('proyNuevo.campo.monto')}</label>
                                    <input
                                        type="text"
                                        inputMode="decimal"
                                        placeholder="0"
                                        className={`${inputClass} font-data tabular-nums`}
                                        value={formData.revenue}
                                        onChange={e => {
                                            // Digits and a single decimal point only — blocks letters and
                                            // the e/+/- that a native number input would otherwise accept.
                                            const raw = e.target.value.replace(/[^\d.]/g, '');
                                            const [whole, ...rest] = raw.split('.');
                                            setFormData({ ...formData, revenue: rest.length ? `${whole}.${rest.join('')}` : whole });
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>{t('proyNuevo.campo.estatus')}</label>
                                    <select
                                        className={inputClass}
                                        value={formData.status}
                                        onChange={e => setFormData({ ...formData, status: e.target.value as ProjectStatus })}
                                    >
                                        <option value={ProjectStatus.SCHEDULED}>{t('proyNuevo.estatus.programado')}</option>
                                        <option value={ProjectStatus.IN_PROGRESS}>{t('proyNuevo.estatus.enProceso')}</option>
                                        <option value={ProjectStatus.COMPLETED}>{t('proyNuevo.estatus.terminado')}</option>
                                        <option value={ProjectStatus.CANCELLED}>{t('proyNuevo.estatus.cancelado')}</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className={labelClass}>{t('proyNuevo.campo.descripcion')}</label>
                                <textarea
                                    className={`${inputClass} resize-none`}
                                    rows={3}
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                />
                            </div>
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={handleCreate}
                                isLoading={isCreating}
                                className="w-full"
                            >
                                {isCreating ? t('proyecto.boton.creando') : t('proyecto.boton.crear')}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewProjectModal;
